import React, { useState, useRef, useEffect, useMemo } from 'react';
import Icon from '@mdi/react';
import { mdiMicrophone, mdiStop, mdiCheck, mdiTimer } from '@mdi/js';
import { Tooltip, Typography } from '@mui/material';
import { IMediaRecorder, IMediaRecorderOptions, MediaRecorder, register, deregister } from 'extendable-media-recorder';
import { connect } from 'extendable-media-recorder-wav-encoder';
import AudioWaves from './AudioWaves';
import { formattedTime } from './utils';
import { useTranslation } from 'react-i18next';
import theme from '../../theme';

const DEFAULT_MAX_RECORDING_TIME = 30 * 60; // 30 minutes

const AudioRecorder = ({stream, onAcquireStream, onReleaseStream, onAudioRecorded, disabled, handleIsRecording, timer, maxAllowedRecordingTime = DEFAULT_MAX_RECORDING_TIME, shouldStop = false} : {
  stream: MediaStream | null,
  onAcquireStream?: () => Promise<MediaStream | null>,
  onReleaseStream?: () => void,
  onAudioRecorded: (blob: Blob) => void,
  disabled?: boolean,
  handleIsRecording?: (value: boolean) => void
  timer?: number
  maxAllowedRecordingTime?: number
  shouldStop?: boolean
 }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>((null));
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<IMediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const encoderRegisteredRef = useRef<MessagePort | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation("localizable");

  useEffect(() => {
    const init = async () => {
      if (!encoderRegisteredRef.current) {
        const encoder: MessagePort = await connect();
        try {
          await register(encoder);
        } catch (e) {
          console.error({e});
        }

        encoderRegisteredRef.current = encoder;
      }
    }

    init();

    return () => {

      const deconstruct = async (encoder: MessagePort) => {
        try {
          await deregister(encoder);
          await encoder.close();
        } catch (e) {
          console.error(e);
        }
      }

      const encoder = encoderRegisteredRef.current;
      if (encoder !== null) {
        deconstruct(encoder);
        encoderRegisteredRef.current = null;
      }
    };

  }, []);

  useEffect(() => {
    if (handleIsRecording) {
      handleIsRecording(isRecording);
    }
  }, [isRecording]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shouldStop && isRecording) {
      handleStopRecording();
    }
  }, [shouldStop]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (audioBlob) {
      onAudioRecorded(audioBlob);
    }
  }, [audioBlob]);

  useEffect(() => {
    if ((timer && recordingTime >= timer) || (maxAllowedRecordingTime && recordingTime >= maxAllowedRecordingTime)) {
      handleStopRecording();
    }

  }, [recordingTime]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prevValue => prevValue + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRecordingTime(0);
    }

  }, [isRecording]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartRecording = async () => {
    let activeStream: MediaStream | null = stream;
    if (!activeStream && onAcquireStream) {
      activeStream = await onAcquireStream();
    }
    if (!activeStream) return;

    const options: IMediaRecorderOptions = {
      audioBitsPerSecond: 128000,
      mimeType: 'audio/wav',
    };

    mediaRecorderRef.current = new MediaRecorder(activeStream, options);

    mediaRecorderRef.current.ondataavailable = (event) => {
      audioChunksRef.current.push(event.data);
    };
    mediaRecorderRef.current.start();
    setIsRecording(true);
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
        audioChunksRef.current = [];
        onReleaseStream?.();
      };

      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const formatTime = useMemo(() => {
    return timer
      ? formattedTime(timer - recordingTime)
      : formattedTime(recordingTime)
  }, [recordingTime]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Tooltip title={isRecording ? t("Stop recording") : t("Start recording")} arrow placement="top">
      <button
        disabled={disabled}
        onClick={isRecording ? handleStopRecording : handleStartRecording}
        style={{
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          color: isRecording ? 'red' : 'white',
          border: 'none',
          background: disabled || isRecording ? 'buttonface' : theme.palette.primary.main,
          transition: 'transform 0.1s',
          transform: isRecording ? 'scale(0.95)' : 'scale(1)',
          boxShadow: '0px 0px 10px 0px gray',
          position: 'relative'
        }}
        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.90)'}
        onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        <>
        <Icon path={isRecording ? mdiStop : mdiMicrophone} size={1.5} />
        {!isRecording && audioBlob && (
          <div style={{position: 'absolute', right:'1px', bottom: '2px'}}>
          <Icon path={mdiCheck} size={0.85} style={{ color: 'white', padding: '2px', backgroundColor: 'green', borderRadius: '50%'}} />
          </div>
         )}
        </>
      </button>
      </Tooltip>
      <Typography variant="subtitle1" component='div' >
        {isRecording
          ? <>
            {timer && <Icon path={mdiTimer} size={0.7} style={{ marginRight: '0.2rem'}} />}
            {formatTime}
            </>
          : audioBlob
            ? t("Redo recording")
            : t("Press to record")}
      </Typography>

      <div className='audio-waves'>
        {stream && <AudioWaves stream={stream} isRecording={isRecording} />}
      </div>
    </div>
  );
};

export default AudioRecorder;

